import type { ObraLimiteClasseDetalhe } from "@/modules/obras/obras.types";
import { TIPO_DOCAGEM_ESPECIAL, TIPO_DOCAGEM_INTERMEDIARIA } from "./nova-obra.tipos";

/**
 * HU 2549 — limites de classe e contrato no wizard. Espelha `avaliarCondicoesObra` do hub-core
 * (`limites-classe.rules.ts`): a mesma classificação decide o aviso, a modal e o que o POST exige
 * em `justificativaCondicoes`. Datas em `AAAA-MM-DD` (comparação lexicográfica = cronológica).
 */

export const JUSTIFICATIVA_CONDICOES_MAX = 500;
export const JUSTIFICATIVA_CONDICOES_OBRIGATORIA = "Informe a justificativa para criar a obra.";
export const JUSTIFICATIVA_CONDICOES_MAXIMO = `A justificativa deve ter no máximo ${JUSTIFICATIVA_CONDICOES_MAX} caracteres.`;

/** Impasse 13: só Especial→101 e Intermediária→102 estão confirmados; as demais não têm tipo. */
const TIPO_QUE_RENOVA: Readonly<Record<string, string>> = {
  ESPECIAL: TIPO_DOCAGEM_ESPECIAL,
  INTERMEDIARIA: TIPO_DOCAGEM_INTERMEDIARIA,
};

export const tipoObraQueRenova = (vistoria: string): string | null =>
  TIPO_QUE_RENOVA[vistoria] ?? null;

/** Relação do limite com o período da obra (D-062); vencido (antes de hoje) prevalece. */
export type RelacaoLimiteObra = "VENCIDO" | "ANTES_DO_INICIO" | "DURANTE_A_OBRA" | "APOS_A_OBRA";

export interface PeriodoCondicoes {
  inicio: string;
  fim: string;
}

export function classificarLimite(
  dataLimite: string,
  periodo: PeriodoCondicoes,
  hoje: string,
): RelacaoLimiteObra {
  if (dataLimite < hoje) return "VENCIDO";
  if (dataLimite < periodo.inicio) return "ANTES_DO_INICIO";
  if (dataLimite <= periodo.fim) return "DURANTE_A_OBRA";
  return "APOS_A_OBRA";
}

export interface LimitePendente {
  vistoria: string;
  descricao: string;
  dataLimite: string;
  relacao: Exclude<RelacaoLimiteObra, "APOS_A_OBRA">;
  /** Código do tipo que renova; `null` = sem tipo associado (sem "Adicionar tipo de obra"). */
  renovaCom: string | null;
}

export interface CondicoesObra {
  limites: LimitePendente[];
  /** Término do contrato quando a obra começa antes dele (RN18); Impasse 3: sem a previsão de extensão. */
  contrato: { terminoContrato: string } | null;
  /** RN16/RN17/RN18/RN21: qualquer ponto pendente exige uma única justificativa. */
  exigeJustificativa: boolean;
}

export const SEM_CONDICOES: CondicoesObra = {
  limites: [],
  contrato: null,
  exigeJustificativa: false,
};

export interface AvaliarCondicoesInput {
  limitesClasse: readonly Pick<ObraLimiteClasseDetalhe, "vistoria" | "descricao" | "dataLimite">[];
  /** `AAAA-MM-DD` ou vazio/`null` (sem término → sem pendência de contrato). */
  terminoContrato: string | null;
  periodo: PeriodoCondicoes;
  /** Códigos dos tipos já adicionados à obra (todos os centros de custo). */
  codigosTiposObra: readonly string[];
  hoje: string;
}

/**
 * Condições pendentes (RN16–RN18): limite vencido, antes do início ou durante a obra cujo tipo que
 * renova não foi adicionado (ou que não tem tipo associado), e contrato que termina depois do
 * início. Limite após a obra não é pendência. Saem ordenados pela data limite.
 */
export function avaliarCondicoesObra(input: AvaliarCondicoesInput): CondicoesObra {
  const { limitesClasse, terminoContrato, periodo, codigosTiposObra, hoje } = input;

  const limites = limitesClasse
    .flatMap<LimitePendente>((limite) => {
      const relacao = classificarLimite(limite.dataLimite, periodo, hoje);
      if (relacao === "APOS_A_OBRA") return [];
      const renovaCom = tipoObraQueRenova(limite.vistoria);
      if (renovaCom !== null && codigosTiposObra.includes(renovaCom)) return [];
      return [{ ...limite, relacao, renovaCom }];
    })
    .sort((a, b) => a.dataLimite.localeCompare(b.dataLimite));

  const contrato = terminoContrato && periodo.inicio < terminoContrato ? { terminoContrato } : null;

  return { limites, contrato, exigeJustificativa: limites.length > 0 || contrato !== null };
}

/** Erro da justificativa (RN22): obrigatória (sem só espaços, trim antes de contar) e de até 500. */
export function validarJustificativaCondicoes(texto: string): string | null {
  const limpo = texto.trim();
  if (limpo === "") return JUSTIFICATIVA_CONDICOES_OBRIGATORIA;
  if (limpo.length > JUSTIFICATIVA_CONDICOES_MAX) return JUSTIFICATIVA_CONDICOES_MAXIMO;
  return null;
}

/** Valor de `justificativaCondicoes` do POST/PUT: o texto aparado com pendência; `null` sem. */
export const justificativaParaEnvio = (
  condicoes: Pick<CondicoesObra, "exigeJustificativa">,
  texto: string,
): string | null => (condicoes.exigeJustificativa ? texto.trim() || null : null);

// ---------------------------------------------------------------- apresentação (relações em dias)

const MS_DIA = 86_400_000;

/** Dias corridos entre duas datas `AAAA-MM-DD` (`b − a`). */
export function diasEntre(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / MS_DIA);
}

const plural = (dias: number) => `${dias} ${Math.abs(dias) === 1 ? "dia" : "dias"}`;

/** Texto auxiliar do limite frente à obra (Figma: "2 dias antes do início", "Venceu há 128 dias"…). */
export function textoRelacaoLimite(
  limite: Pick<LimitePendente, "dataLimite" | "relacao">,
  periodo: PeriodoCondicoes,
  hoje: string,
): string {
  switch (limite.relacao) {
    case "VENCIDO":
      return `Venceu há ${plural(diasEntre(limite.dataLimite, hoje))}`;
    case "ANTES_DO_INICIO":
      return `${plural(diasEntre(limite.dataLimite, periodo.inicio))} antes do início`;
    case "DURANTE_A_OBRA":
      return "Durante a obra";
  }
}

/** Relação genérica (qualquer limite, para a consulta "Ver todos" com período). */
export function textoRelacaoObra(
  dataLimite: string,
  periodo: PeriodoCondicoes,
  hoje: string,
): { relacao: RelacaoLimiteObra; texto: string } {
  const relacao = classificarLimite(dataLimite, periodo, hoje);
  if (relacao === "APOS_A_OBRA") {
    return {
      relacao,
      texto: `${plural(diasEntre(periodo.fim, dataLimite))} depois do término`,
    };
  }
  return { relacao, texto: textoRelacaoLimite({ dataLimite, relacao }, periodo, hoje) };
}

/** "Faltam N dias" / "Venceu há N dias" (coluna Vencimento da consulta). */
export function textoVencimento(dataLimite: string, hoje: string): string {
  const dias = diasEntre(hoje, dataLimite);
  if (dias < 0) return `Venceu há ${plural(-dias)}`;
  if (dias === 0) return "Vence hoje";
  return `Faltam ${plural(dias)}`;
}

/**
 * Janela de "a vencer" da coluna Vencimento (Figma 10229:689104: "Faltam 49 dias" em laranja,
 * "Faltam 100 dias" em cinza). O corte exato não está na HU; 90 dias fica entre os dois exemplos.
 */
export const DIAS_VENCIMENTO_PROXIMO = 90;

/** Vencimento em destaque (laranja): vencido, vence hoje ou dentro da janela de "a vencer". */
export const vencimentoEmDestaque = (dataLimite: string, hoje: string): boolean =>
  diasEntre(hoje, dataLimite) <= DIAS_VENCIMENTO_PROXIMO;

/** Texto do contrato frente à obra (Figma: "Termina durante a obra", "Termina 44 dias depois da obra"). */
export function textoRelacaoContrato(terminoContrato: string, periodo: PeriodoCondicoes): string {
  return terminoContrato <= periodo.fim
    ? "Termina durante a obra"
    : `Termina ${plural(diasEntre(periodo.fim, terminoContrato))} depois da obra`;
}

// ---------------------------------------------------------------- mensagens (MS001–MS011)

/** Artigo da vistoria ("a Intermediária", "o FMEA"); só Especial e Intermediária renovam. */
const ARTIGO_VISTORIA: Readonly<Record<string, "a" | "o">> = {
  ESPECIAL: "a",
  INTERMEDIARIA: "a",
  ANUAL: "a",
  PROPULSAO: "a",
};
const artigo = (vistoria: string) => ARTIGO_VISTORIA[vistoria] ?? "o";

export type FormatarData = (iso: string) => string;
export type RotuloTipo = (codigo: string) => string;

/**
 * Aviso da etapa Tipos de Obra (MS001/MS002): só limites; some quando nenhum fica pendente.
 * `rotulo` devolve "102 · Docagem Intermediária" (nomenclatura vigente do catálogo).
 */
export function mensagemAvisoTipos(
  limites: readonly LimitePendente[],
  data: FormatarData,
  rotulo: RotuloTipo,
): string | null {
  if (limites.length === 0) return null;

  if (limites.length === 1) {
    const [limite] = limites as [LimitePendente];
    const quando = {
      VENCIDO: `venceu em ${data(limite.dataLimite)}`,
      ANTES_DO_INICIO: `vence em ${data(limite.dataLimite)}, antes do início da obra`,
      DURANTE_A_OBRA: `vence em ${data(limite.dataLimite)}, durante a obra`,
    }[limite.relacao];
    const acao = limite.renovaCom
      ? `Para renovar, adicione ${rotulo(limite.renovaCom)}.`
      : "Esse limite não tem tipo de obra associado.";
    return `O limite de classe ${limite.descricao} ${quando}. ${acao}`;
  }

  const lista = limites.map((limite) => `${limite.descricao} (${data(limite.dataLimite)})`);
  const nomes =
    lista.length === 2 ? lista.join(" e ") : `${lista.slice(0, -1).join(", ")} e ${lista.at(-1)}`;
  const renovaveis = limites
    .filter((limite) => limite.renovaCom)
    .map(
      (limite) =>
        `Para renovar ${artigo(limite.vistoria)} ${limite.descricao}, adicione ${rotulo(limite.renovaCom ?? "")}.`,
    );
  const semTipo = limites
    .filter((limite) => !limite.renovaCom)
    .map(
      (limite) =>
        `${artigo(limite.vistoria).toUpperCase()} ${limite.descricao} não tem tipo de obra associado.`,
    );

  return [
    `Os limites de classe ${nomes} vencem antes do início ou durante a obra.`,
    ...renovaveis,
    ...semTipo,
  ].join(" ");
}

export interface MensagemRevisao {
  /** Chave estável: um limite por vistoria, mais `contrato`. */
  chave: string;
  tipo: "limite" | "contrato";
  /** Parte em negrito antes dos dois-pontos ("Limite de classe Especial"). */
  titulo: string;
  texto: string;
}

const PEDIRA_JUSTIFICATIVA = "Ao criar a obra, será pedida uma justificativa.";

/** Revisão (RN11/RN12): uma mensagem por ponto; limite de classe e contrato são pontos distintos. */
export function mensagensRevisao(
  condicoes: CondicoesObra,
  data: FormatarData,
  rotulo: RotuloTipo,
): MensagemRevisao[] {
  const limites = condicoes.limites.map<MensagemRevisao>((limite) => {
    const titulo = `Limite de classe ${limite.descricao}`;
    const tipo = limite.renovaCom ? rotulo(limite.renovaCom) : null;
    const ausencia = tipo
      ? `a obra não inclui ${tipo}`
      : "o Hub não associa tipo de obra à renovação desse limite";
    const texto =
      limite.relacao === "VENCIDO"
        ? `venceu em ${data(limite.dataLimite)} e ${ausencia}.`
        : `vence ${limite.relacao === "ANTES_DO_INICIO" ? "antes do início" : "durante a obra"} e ${ausencia}.`;

    return {
      chave: limite.vistoria,
      tipo: "limite",
      titulo,
      texto: `${texto} ${PEDIRA_JUSTIFICATIVA}`,
    };
  });

  const contrato: MensagemRevisao[] = condicoes.contrato
    ? [
        {
          chave: "contrato",
          tipo: "contrato",
          titulo: "Contrato",
          texto: `a obra começa antes do fim do contrato atual (${data(condicoes.contrato.terminoContrato)}). ${PEDIRA_JUSTIFICATIVA}`,
        },
      ]
    : [];

  return [...limites, ...contrato];
}

// ---------------------------------------------------------------- modal de justificativa (MS006–MS011)

export type VarianteJustificativa =
  "limite" | "vencido" | "sem-tipo" | "contrato" | "limite-contrato" | "varios";

export interface TextosJustificativa {
  variante: VarianteJustificativa;
  titulo: string;
  /** `**negrito**` marca os trechos em destaque. */
  descricao: string;
  pergunta: string;
  apoio: string;
  /** Pergunta em vermelho (limite já vencido, Figma "Limite de classe vencido"). */
  perigo: boolean;
  /** "Adicionar tipo de obra" só quando algum limite pendente tem tipo associado (RN24). */
  podeAdicionarTipo: boolean;
}

function descreverLimite(
  limite: LimitePendente,
  hoje: string,
  data: FormatarData,
  rotulo: RotuloTipo,
): string {
  const lacuna = limite.renovaCom
    ? `a obra não inclui **${rotulo(limite.renovaCom)}**, que renova esse limite`
    : "o Hub não associa nenhum tipo de obra à renovação desse limite";
  // Figma: a data só vai em negrito no caso vencido, junto com "há N dias".
  const quando = {
    VENCIDO: `venceu em **${data(limite.dataLimite)}, há ${plural(diasEntre(limite.dataLimite, hoje))}**`,
    ANTES_DO_INICIO: `vence em ${data(limite.dataLimite)}, antes do início da obra`,
    DURANTE_A_OBRA: `vence em ${data(limite.dataLimite)}, durante a obra`,
  }[limite.relacao];
  return `O limite de classe **${limite.descricao}** ${quando}, e ${lacuna}.`;
}

/** Título, texto e pergunta da modal conforme os pontos pendentes (uma modal, variantes por caso). */
export function textosJustificativa(
  condicoes: CondicoesObra,
  periodo: PeriodoCondicoes,
  hoje: string,
  data: FormatarData,
  rotulo: RotuloTipo,
): TextosJustificativa {
  const { limites, contrato } = condicoes;
  const podeAdicionarTipo = limites.some((limite) => limite.renovaCom !== null);

  if (limites.length === 0) {
    const termino = contrato?.terminoContrato ?? "";
    return {
      variante: "contrato",
      titulo: "Obra antes do fim do contrato",
      descricao: `A obra começa em **${data(periodo.inicio)}**, antes do término do contrato atual da embarcação, em **${data(termino)}**. Parar a embarcação antes do fim do contrato gera downtime.`,
      pergunta: "Por que começar a obra antes do fim do contrato?",
      apoio: `Se a obra puder esperar, ajuste o início para depois de ${data(termino)}.`,
      perigo: false,
      podeAdicionarTipo,
    };
  }

  if (limites.length === 1 && !contrato) {
    const [limite] = limites as [LimitePendente];
    const vencido = limite.relacao === "VENCIDO";
    const descricao = descreverLimite(limite, hoje, data, rotulo);

    if (!limite.renovaCom) {
      return {
        variante: vencido ? "vencido" : "sem-tipo",
        titulo: vencido ? "Limite de classe vencido" : "Limite de classe sem tipo de obra",
        descricao,
        pergunta: `Como a vistoria ${limite.descricao} será tratada nesta obra?`,
        apoio: "Ex.: vistoria agendada à parte com a classificadora.",
        perigo: vencido,
        podeAdicionarTipo,
      };
    }
    return vencido
      ? {
          variante: "vencido",
          titulo: "Limite de classe vencido",
          descricao,
          pergunta: "Por que criar a obra com o limite vencido e sem a docagem?",
          apoio: "Se a classificadora concedeu extensão, informe a nova data na justificativa.",
          perigo: true,
          podeAdicionarTipo,
        }
      : {
          variante: "limite",
          titulo: `Obra sem docagem ${limite.descricao}`,
          descricao,
          pergunta: `Por que criar a obra sem renovar o limite ${limite.descricao}?`,
          apoio: "Ex.: a renovação será feita em outra obra, ou a classificadora estendeu o prazo.",
          perigo: false,
          podeAdicionarTipo,
        };
  }

  if (limites.length === 1) {
    const [limite] = limites as [LimitePendente];
    const situacao = {
      VENCIDO: `venceu em **${data(limite.dataLimite)}, há ${plural(diasEntre(limite.dataLimite, hoje))}**`,
      ANTES_DO_INICIO: "vence antes do início",
      DURANTE_A_OBRA: "vence durante a obra",
    }[limite.relacao];
    const lacuna = limite.renovaCom
      ? `a obra não inclui **${rotulo(limite.renovaCom)}**`
      : "o Hub não associa tipo de obra à renovação desse limite";
    return {
      variante: "limite-contrato",
      titulo: "Limite de classe e contrato",
      descricao: `Há **dois pontos para justificar**. O limite de classe **${limite.descricao}** ${situacao} e ${lacuna}. E a obra começa antes do fim do contrato atual da embarcação.`,
      pergunta: `Por que criar a obra sem renovar o limite ${limite.descricao} e antes do fim do contrato?`,
      apoio: "Explique os dois pontos: o limite de classe e o contrato.",
      perigo: false,
      podeAdicionarTipo,
    };
  }

  const nomes = limites.map((limite) => limite.descricao);
  const lista = `${nomes.slice(0, -1).join(", ")} e ${nomes.at(-1)}`;
  return contrato
    ? {
        variante: "varios",
        titulo: "Limite de classe e contrato",
        descricao: `Há **dois pontos para justificar**: os limites de classe **${lista}** vencem antes do início ou durante a obra, sem a docagem que os renova, e a obra começa antes do fim do contrato atual.`,
        pergunta: "Por que criar a obra sem renovar esses limites e antes do fim do contrato?",
        apoio: "Explique como cada limite será tratado e por que começar antes do fim do contrato.",
        perigo: false,
        podeAdicionarTipo,
      }
    : {
        variante: "varios",
        titulo: "Limites de classe",
        descricao: `Os limites de classe **${lista}** vencem antes do início ou durante a obra, sem a docagem que os renova.`,
        pergunta: "Por que criar a obra sem renovar esses limites?",
        apoio: "Explique como cada limite será tratado.",
        perigo: false,
        podeAdicionarTipo,
      };
}
